import { useEffect, useRef, useState } from "react";

/**
 * SpotlightBeforeAfter — replaces the traditional drag slider.
 * Uses a cursor-following radial mask to reveal the "after" (clean) image
 * behind the "before" (dirty) image. Feels like moving a spotlight across
 * the storefront to inspect the work.
 *
 * Falls back gracefully on touch: user drags finger; if idle, spotlight orbits.
 */
export default function SpotlightBeforeAfter({ before, after, label, testid = "spotlight-ba" }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState({ x: 0.5, y: 0.5 });
  const [active, setActive] = useState(false);
  const idleTimer = useRef(null);
  const orbitRaf = useRef(0);
  const orbitT = useRef(0);

  useEffect(() => {
    // Gentle orbit when idle to invite interaction
    function orbit() {
      orbitT.current += 0.008;
      if (!active) {
        setPos({
          x: 0.5 + Math.cos(orbitT.current) * 0.28,
          y: 0.5 + Math.sin(orbitT.current * 0.85) * 0.20,
        });
      }
      orbitRaf.current = requestAnimationFrame(orbit);
    }
    orbitRaf.current = requestAnimationFrame(orbit);
    return () => cancelAnimationFrame(orbitRaf.current);
  }, [active]);

  function onMove(e) {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    setPos({
      x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)),
    });
    setActive(true);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setActive(false), 2500);
  }

  const spotSize = 340; // px diameter of the reveal
  const maskStyle = {
    WebkitMaskImage: `radial-gradient(circle ${spotSize}px at ${pos.x * 100}% ${pos.y * 100}%, black 20%, transparent 60%)`,
    maskImage: `radial-gradient(circle ${spotSize}px at ${pos.x * 100}% ${pos.y * 100}%, black 20%, transparent 60%)`,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden select-none"
      style={{
        aspectRatio: "16 / 10",
        borderRadius: "28px",
        border: "1px solid var(--line-gold)",
        boxShadow: "0 40px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.03) inset",
        cursor: "crosshair",
      }}
      onMouseMove={onMove}
      onTouchMove={onMove}
      onTouchStart={onMove}
      data-testid={testid}
      data-cursor="text"
      data-cursor-label="Inspect"
    >
      {/* Base BEFORE image (dirty) */}
      <img
        src={before}
        alt="Before"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "brightness(0.72) contrast(1.05) saturate(0.85)" }}
        draggable={false}
        loading="lazy"
      />

      {/* AFTER image (clean) revealed inside spotlight */}
      <img
        src={after}
        alt="After"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          ...maskStyle,
          filter: "brightness(0.9) contrast(1.1) saturate(1.05)",
        }}
        draggable={false}
        loading="lazy"
      />

      {/* Spotlight halo (visible bloom around cursor) */}
      <div
        aria-hidden
        className="absolute pointer-events-none transition-opacity duration-500"
        style={{
          left: `${pos.x * 100}%`,
          top: `${pos.y * 100}%`,
          width: spotSize * 2,
          height: spotSize * 2,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(232,217,197,0.22) 0%, rgba(198,168,124,0.06) 35%, transparent 60%)",
          mixBlendMode: "screen",
          opacity: 0.9,
        }}
      />

      {/* Fine reveal edge (subtle bright ring) */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          left: `${pos.x * 100}%`,
          top: `${pos.y * 100}%`,
          width: spotSize,
          height: spotSize,
          transform: "translate(-50%, -50%)",
          border: "1px solid rgba(232,217,197,0.28)",
          borderRadius: "999px",
          boxShadow: "0 0 40px rgba(232,217,197,0.15) inset, 0 0 60px rgba(198,168,124,0.12)",
        }}
      />

      {/* Corner labels */}
      <div className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.36em] px-3 py-1.5" style={{
        background: "rgba(0,0,0,0.55)",
        color: "var(--text-2)",
        borderRadius: "999px",
        border: "1px solid rgba(255,255,255,0.08)",
        backdropFilter: "blur(8px)",
      }}>
        Before
      </div>
      <div className="absolute top-5 right-5 font-mono text-[10px] uppercase tracking-[0.36em] px-3 py-1.5" style={{
        background: "rgba(198,168,124,0.18)",
        color: "var(--gold-2)",
        borderRadius: "999px",
        border: "1px solid var(--line-gold)",
        backdropFilter: "blur(8px)",
      }}>
        After
      </div>
      {label && (
        <div className="absolute bottom-5 left-5 right-5 font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
          {label}
        </div>
      )}

      {/* Idle prompt */}
      {!active && (
        <div
          className="absolute bottom-5 right-5 font-mono text-[10px] uppercase tracking-[0.32em] px-3 py-1.5 animate-pulse"
          style={{ color: "var(--gold-2)", background: "rgba(0,0,0,0.45)", borderRadius: "999px", border: "1px solid var(--line-gold)" }}
        >
          Move to inspect
        </div>
      )}
    </div>
  );
}
